// i18n area 'system' (TVirt task #127 layout, adopted for TVStor M1): section
// users. The groups/realms tabs, RBAC, credentials and the rest of the TVirt
// system vocabulary join with their milestones.

export const en = {
  users: {
    title: 'Users',
    createTitle: 'Create user',
    editTitle: 'Edit user',
    username: 'Username',
    usernameHint: 'Lowercase letters, digits, dots and dashes',
    displayName: 'Display name',
    enabled: 'Enabled',
    state: 'State',
    enabledChip: 'Enabled',
    disabledChip: 'Disabled',
    lockedChip: 'Locked',
    password: 'Password',
    passwordSetChip: 'Set',
    mustChangeChip: 'Must change it',
    initialPassword: 'Initial password',
    initialPasswordHint: 'The user must change it at first login',
    lastLogin: 'Last login',
    neverLoggedIn: 'Never logged in',
    setPasswordTitle: 'Set password',
    setPasswordHint: 'Administrative replace — it requires your password (sudo mode) and forces the user to change it at the next login.',
    operatorPassword: 'Your password',
    unlock: 'Unlock the account',

    // #181 ondata 1c (T8): the HARD half said in full — the contract destroys
    // the editor tokens with the sessions, and unsaved work is lost.
    revokeTitle: 'Revoke sessions',
    revokeBody:
      'Every active session of this user is terminated immediately, and the document-editor tokens those sessions minted are destroyed — an open editor stops saving within seconds and unsaved work is LOST. The account keeps existing and can log in again.',
    revokeConfirm: 'Revoke',
    revokeDone: '{sessions} session(s) revoked, {tokens} editor token(s) destroyed.',

    // Dialog standard (TVirt round #156): complete details, tabbed account/security.
    tabAccount: 'Account',
    tabSecurity: 'Security',
    usernameImmutableHint: "The username is the account's identity — fixed at creation",
    mustChangePassword: 'Password change required',
    failedAttempts: 'Failed login attempts',
    lockedUntil: 'Locked until',

    // REFUI-1 (dettatura 4, GUI structure only): the Users page grows the
    // three subsections — local users (the existing view), external
    // authentication and MFA. #181 ondata 1a made External the DirectoryRealms
    // page; ondata 1b made MFA the credential-state console below.
    tabs: {
      local: 'Local users',
      external: 'External authentication',
      mfa: 'MFA',
      sessions: 'Sessions',
      grants: 'Grants',
      roles: 'Roles'
    },

    // #181 ondata 1b: the credential's TRUE MFA state on the list and the
    // detail — chips for the enrolment/delegation, the obligation with its
    // sudo-gated gesture, and the admin reset (user.mfa.reset + sudo).
    mfaColumn: 'MFA',
    mfaTotpChip: 'TOTP',
    mfaExternalChip: 'External MFA',
    mfaRequiredChip: 'Required',

    // #181 ondata 5 (ratifica §6): the mfa* filters travel SERVER-side
    // (?filter= on the contract's own axes) — never a client sieve.
    mfaFilterEnrolled: 'TOTP enrolled',
    mfaFilterExternal: 'External MFA',
    mfaFilterRequired: 'MFA required',
    mfaFilterAll: 'All',
    mfaFilterYes: 'Yes',
    mfaFilterNo: 'No',
    mfaEnrolledField: 'TOTP second factor',
    mfaEnrolledSince: 'enrolled {date}',
    mfaNotEnrolled: 'not enrolled',
    mfaExternalField: 'External MFA delegation',
    mfaExternalHint:
      'Delegates the second factor to the cluster\'s external MFA server (privacyIDEA) — an authentication-policy statement, admin-writable, mutually exclusive with a local TOTP enrolment (the server refuses while one is confirmed).',
    mfaRequiredField: 'Second-factor obligation',
    mfaRequiredHint: 'Sudo-protected mutation — impose or clear it from the MFA tab of this page.',
    mfaRequiredSince: 'required since {date}',

    // #181 ondata 1a: the External tab is no longer a placeholder — it hosts
    // the DirectoryRealms page (i18n area 'realms'). OIDC/SAML remain a
    // declared fence with their own future round (censimento).
    mfa: {
      title: 'MFA',

      // The cluster knobs the state below is judged against — editable HERE
      // since SETTINGS-1 (#196, 2026-08-21): the sudo-gated singleton PATCH
      // behind the two pencils of the banner.
      graceKnob: 'Enrolment grace window: {days} days (cluster.settings.mfaGraceDays)',
      externalKnobConfigured: 'External MFA server: configured ({url})',
      externalKnobNone: 'External MFA server: not configured — logins of delegated credentials answer 409 mfa_not_configured',

      // #187 MFA-ISSUER: the TOTP QR issuer — future enrolments only
      // (an authenticator keeps the name it stored, declared in contract).
      issuerKnobConfigured: 'TOTP QR issuer: "{issuer}" (cluster.settings.mfaIssuer — future enrolments only)',
      issuerKnobDefault: 'TOTP QR issuer: the default "TVStor ⟨realm⟩" (cluster.settings.mfaIssuer not set)',

      // SETTINGS-1 (#196): the hint stops pointing away — the console edits
      // the knobs right here; the CLI twin covers grace+issuer
      // (`cluster set-mfa`), the external server has no CLI verb yet.
      knobsHint: 'Cluster-wide knobs on the sudo-protected cluster settings PATCH — editable right here (grace and issuer also via `tvstor cluster set-mfa`; the external server is console/API only today).',

      // SETTINGS-1 (#196): the two edit dialogs of the banner.
      knobsEditAction: 'Edit the MFA knobs',
      knobsTitle: 'Cluster MFA knobs',
      knobsWarning:
        'A LONGER grace window keeps password-only logins of obligated credentials accepted for longer — a weaker posture, stated. A SHORTER one moves every RUNNING deadline closer (deadline = mfaRequiredAt + the new value): a user still enrolling may find the next login refused 401 mfa_enrollment_required. 0 = refusal from the next login on.',
      graceLabel: 'Grace window (days)',
      graceHint: '0-365 — 0 is the lever for a credential believed compromised',
      graceDefaultLabel: 'Return to the platform default (14 days)',
      issuerLabel: 'TOTP QR issuer',
      issuerHint: '1-64 characters, never a colon (the otpauth key-uri separator) — future enrolments only: an authenticator keeps the name it stored',
      issuerDefaultLabel: 'Use the default "TVStor ⟨realm⟩"',
      knobsError: 'The knobs write was refused',
      externalEditAction: 'Configure the external MFA server',
      externalTitle: 'External MFA server (privacyIDEA)',
      externalConfigureWarning:
        'A third-party destination: the login hands this server usernames and LIVE one-time codes (/validate/check). HTTPS only; a non-loopback server needs the TLS pin below — without it, delegated logins refuse naming the missing anchor.',
      externalRemoveLabel: 'Remove the external server (merge-patch null)',
      externalRemoveAction: 'Remove the server',
      externalRemoveWarning:
        'Removing the server does NOT relax anything for delegated credentials: their logins answer 409 mfa_not_configured — locked out until the delegation is cleared per user or a server is configured again.',
      serverUrlLabel: 'Server URL',
      serverUrlHint: 'https:// only — the request carries a username and a live one-time code',
      realmLabel: 'privacyIDEA realm (optional)',
      realmHint: 'Passed through as the realm parameter of /validate/check; empty = the server\'s own default realm',
      fingerprintLabel: 'TLS pin (sha-256 of the served certificate)',
      fingerprintHint: '64 hex characters, colon spelling accepted — the anchor of every /validate/check; optional only for a loopback server URL',
      externalError: 'The external-MFA write was refused',

      state: 'Second factor',
      obligation: 'Obligation',
      noFactor: 'none yet',
      graceDeadline: 'grace ends {date}',
      graceDeadlineNote: 'deadline = mfaRequiredAt + mfaGraceDays (contract formula)',

      requireAction: 'Require a second factor',
      clearAction: 'Clear the obligation',
      obligationTitle: 'Second-factor obligation',
      requireBody:
        'Marks the credential as REQUIRING a second factor (MFA.2): the user has the grace window to enrol — during it every password-only login carries the deadline warning; past it the login answers 401 mfa_enrollment_required. Sudo-protected.',
      clearBody:
        'Clears the obligation: the credential may keep logging in with the password alone (an existing enrolment stays active). Sudo-protected.',
      resetAction: 'Reset the second factor',
      resetTitle: 'Reset the second factor (administrative)',
      resetBody:
        'The recovery lever for the user whose authenticator AND recovery codes are gone: destroys the sealed secret and the remaining codes, the login returns password-only. Requires the user.mfa.reset grain plus sudo; audited like every mutation.',
      actionError: 'The MFA action was refused'
    }
  },

  // #181 ondata 1c — the sessions console (SEC-REVOKE #79): the group
  // photograph with the server-side ?realm= incident filter and the scoped
  // revoke by id. Visibility is the contract's: every row shown is a row the
  // caller may also revoke.
  sessions: {
    title: 'Sessions',
    user: 'Session',
    realm: 'Realm',
    createdAt: 'Opened at',
    expiresAt: 'Idle deadline',
    currentChip: 'This session',
    realmFilterLabel: 'Realm',
    realmAll: 'All realms',
    visibilityHint:
      'Live web sessions, newest first. You always see your own; another principal\'s sessions appear only within the reach of your session-revoke grant — every row shown is also a row you may revoke. Filter by realm server-side: "files" is the incident view — every web-explorer session of the file services.',
    revokeAction: 'Revoke this session',
    revokeTitle: 'Revoke the session of {name}?',
    revokeBody:
      'The session dies now: the next request with its token is refused, and its holder must log in again. Safe to retry — revoking an already-revoked session answers the same. To throw a file-services account out everywhere at once, use "Revoke web sessions" on its credential; to empty a whole realm, the realm\'s own revoke.',
    revokeOwnWarning: 'This is YOUR current session — revoking it is a logout: you land back on the login page.',
    revokeSubmit: 'Revoke'
  }
}

export const it: typeof en = {
  users: {
    title: 'Utenti',
    createTitle: 'Crea utente',
    editTitle: 'Modifica utente',
    username: 'Nome utente',
    usernameHint: 'Minuscole, cifre, punti e trattini',
    displayName: 'Nome visualizzato',
    enabled: 'Abilitato',
    state: 'Stato',
    enabledChip: 'Abilitato',
    disabledChip: 'Disabilitato',
    lockedChip: 'Bloccato',
    password: 'Password',
    passwordSetChip: 'Impostata',
    mustChangeChip: 'Da cambiare',
    initialPassword: 'Password iniziale',
    initialPasswordHint: "L'utente dovrà cambiarla al primo accesso",
    lastLogin: 'Ultimo accesso',
    neverLoggedIn: 'Mai entrato',
    setPasswordTitle: 'Imposta password',
    setPasswordHint: "Sostituzione amministrativa — richiede la tua password (modalità sudo) e obbliga l'utente a cambiarla al prossimo accesso.",
    operatorPassword: 'La tua password',
    unlock: "Sblocca l'account",
    revokeTitle: 'Revoca sessioni',
    revokeBody:
      "Tutte le sessioni attive di questo utente vengono terminate immediatamente, e i token dell'editor documenti coniati da quelle sessioni vengono distrutti — un editor aperto smette di salvare entro pochi secondi e il lavoro non salvato è PERSO. L'account continua a esistere e può rifare il login.",
    revokeConfirm: 'Revoca',
    revokeDone: '{sessions} sessioni revocate, {tokens} token editor distrutti.',
    tabAccount: 'Account',
    tabSecurity: 'Sicurezza',
    usernameImmutableHint: "Il nome utente è l'identità dell'account — fissato alla creazione",
    mustChangePassword: 'Cambio password obbligatorio',
    failedAttempts: 'Tentativi di accesso falliti',
    lockedUntil: 'Bloccato fino a',
    tabs: {
      local: 'Utenti locali',
      external: 'Autenticazione esterna',
      mfa: 'MFA',
      sessions: 'Sessioni',
      grants: 'Deleghe',
      roles: 'Ruoli'
    },
    mfaColumn: 'MFA',
    mfaTotpChip: 'TOTP',
    mfaExternalChip: 'MFA esterna',
    mfaRequiredChip: 'Obbligatoria',
    mfaFilterEnrolled: 'TOTP attivo',
    mfaFilterExternal: 'MFA esterna',
    mfaFilterRequired: 'MFA obbligatoria',
    mfaFilterAll: 'Tutti',
    mfaFilterYes: 'Sì',
    mfaFilterNo: 'No',
    mfaEnrolledField: 'Secondo fattore TOTP',
    mfaEnrolledSince: 'attivato il {date}',
    mfaNotEnrolled: 'non attivo',
    mfaExternalField: 'Delega MFA esterna',
    mfaExternalHint:
      'Delega il secondo fattore al server MFA esterno del cluster (privacyIDEA) — una dichiarazione di policy di autenticazione, scrivibile dall\'amministratore, mutuamente esclusiva con l\'arruolamento TOTP locale (il server rifiuta finché ne esiste uno confermato).',
    mfaRequiredField: 'Obbligo di secondo fattore',
    mfaRequiredHint: 'Mutazione protetta da sudo — si impone o si toglie dal tab MFA di questa pagina.',
    mfaRequiredSince: 'obbligo dal {date}',
    mfa: {
      title: 'MFA',
      graceKnob: 'Finestra di grazia per l\'arruolamento: {days} giorni (cluster.settings.mfaGraceDays)',
      externalKnobConfigured: 'Server MFA esterno: configurato ({url})',
      externalKnobNone: 'Server MFA esterno: non configurato — i login delle credenziali con delega rispondono 409 mfa_not_configured',

      // #187 MFA-ISSUER: l'issuer del QR TOTP — solo gli arruolamenti futuri.
      issuerKnobConfigured: 'Issuer del QR TOTP: "{issuer}" (cluster.settings.mfaIssuer — solo gli arruolamenti futuri)',
      issuerKnobDefault: 'Issuer del QR TOTP: il default "TVStor ⟨realm⟩" (cluster.settings.mfaIssuer non impostato)',

      // SETTINGS-1 (#196): l'hint smette di indicare altrove — l'edit è qui.
      knobsHint: 'Manopole a livello cluster sul PATCH delle impostazioni protetto da sudo — modificabili proprio qui (grazia e issuer anche con `tvstor cluster set-mfa`; il server esterno oggi solo console/API).',

      // SETTINGS-1 (#196): le due dialog di edit del banner.
      knobsEditAction: 'Modifica le manopole MFA',
      knobsTitle: 'Manopole MFA del cluster',
      knobsWarning:
        'Una finestra di grazia PIÙ LUNGA accetta più a lungo i login a sola password delle credenziali obbligate — postura più debole, detto chiaro. Una PIÙ CORTA avvicina ogni scadenza IN CORSO (scadenza = mfaRequiredAt + il nuovo valore): un utente ancora in arruolamento può trovarsi il prossimo login rifiutato 401 mfa_enrollment_required. 0 = rifiuto dal prossimo login.',
      graceLabel: 'Finestra di grazia (giorni)',
      graceHint: '0-365 — 0 è la leva per una credenziale che si crede compromessa',
      graceDefaultLabel: 'Torna al default di piattaforma (14 giorni)',
      issuerLabel: 'Issuer del QR TOTP',
      issuerHint: '1-64 caratteri, mai i due punti (il separatore del formato key-uri otpauth) — solo arruolamenti futuri: l\'authenticator tiene il nome che ha memorizzato',
      issuerDefaultLabel: 'Usa il default "TVStor ⟨realm⟩"',
      knobsError: 'La scrittura delle manopole è stata rifiutata',
      externalEditAction: 'Configura il server MFA esterno',
      externalTitle: 'Server MFA esterno (privacyIDEA)',
      externalConfigureWarning:
        'Una destinazione di terze parti: il login consegna a questo server username e codici usa-e-getta VIVI (/validate/check). Solo HTTPS; un server non-loopback richiede il pin TLS qui sotto — senza, i login con delega rifiutano nominando l\'ancora mancante.',
      externalRemoveLabel: 'Rimuovi il server esterno (merge-patch null)',
      externalRemoveAction: 'Rimuovi il server',
      externalRemoveWarning:
        'Rimuovere il server NON allenta nulla per le credenziali con delega: i loro login rispondono 409 mfa_not_configured — fuori dalla console finché non si toglie la delega utente per utente o non si riconfigura un server.',
      serverUrlLabel: 'URL del server',
      serverUrlHint: 'Solo https:// — la richiesta trasporta uno username e un codice usa-e-getta vivo',
      realmLabel: 'Realm privacyIDEA (opzionale)',
      realmHint: 'Passato com\'è come parametro realm di /validate/check; vuoto = il realm di default del server',
      fingerprintLabel: 'Pin TLS (sha-256 del certificato servito)',
      fingerprintHint: '64 caratteri esadecimali, accettata la grafia con i due punti — l\'ancora di ogni /validate/check; opzionale solo per un server loopback',
      externalError: 'La scrittura del server MFA esterno è stata rifiutata',
      state: 'Secondo fattore',
      obligation: 'Obbligo',
      noFactor: 'ancora nessuno',
      graceDeadline: 'grazia fino al {date}',
      graceDeadlineNote: 'scadenza = mfaRequiredAt + mfaGraceDays (formula del contract)',
      requireAction: 'Richiedi un secondo fattore',
      clearAction: 'Togli l\'obbligo',
      obligationTitle: 'Obbligo di secondo fattore',
      requireBody:
        'Marca la credenziale come OBBLIGATA al secondo fattore (MFA.2): l\'utente ha la finestra di grazia per arruolarsi — durante la finestra ogni login a sola password porta l\'avviso con la scadenza; oltre, il login risponde 401 mfa_enrollment_required. Protetto da sudo.',
      clearBody:
        'Toglie l\'obbligo: la credenziale può continuare a entrare con la sola password (un arruolamento esistente resta attivo). Protetto da sudo.',
      resetAction: 'Azzera il secondo fattore',
      resetTitle: 'Azzera il secondo fattore (amministrativo)',
      resetBody:
        'La leva di recupero per l\'utente che ha perso authenticator E codici di recupero: distrugge il segreto sigillato e i codici rimasti, l\'accesso torna a sola password. Richiede il grano user.mfa.reset più sudo; finisce in audit come ogni mutazione.',
      actionError: 'L\'azione MFA è stata rifiutata'
    }
  },
  sessions: {
    title: 'Sessioni',
    user: 'Sessione',
    realm: 'Realm',
    createdAt: 'Aperta il',
    expiresAt: 'Scadenza di inattività',
    currentChip: 'Questa sessione',
    realmFilterLabel: 'Realm',
    realmAll: 'Tutti i realm',
    visibilityHint:
      'Le sessioni web vive, dalla più recente. Le tue le vedi sempre; quelle di un altro principal compaiono solo entro la portata del tuo permesso di revoca sessioni — ogni riga mostrata è anche una riga che puoi revocare. Il filtro per realm è lato server: «files» è la vista d\'incidente — ogni sessione dell\'esploratore web dei servizi file.',
    revokeAction: 'Revoca questa sessione',
    revokeTitle: 'Revocare la sessione di {name}?',
    revokeBody:
      'La sessione muore adesso: la prossima richiesta col suo token viene rifiutata, e chi la deteneva deve rifare il login. Riprovare è sicuro — revocare una sessione già revocata risponde lo stesso. Per cacciare un account di servizi file ovunque in un colpo, usa «Revoca le sessioni web» sulla sua credenziale; per svuotare un intero realm, la revoca del realm.',
    revokeOwnWarning: 'Questa è la TUA sessione corrente — revocarla è un logout: torni alla pagina di accesso.',
    revokeSubmit: 'Revoca'
  }
}
